import React, { useState } from 'react';
import ContentLoader from 'react-content-loader';
import cx from 'classnames';

import SelectableListPopover from '@arrive/selectable-list-popover';
import LaneSortingDropdown from 'components/CPCommittedCapacity/components/LaneWidget/LaneSortingDropdown';
import SVGIcon from '@arrive/svg-icon';

import { SortByType, SortDirectionEnum } from 'sharedTypes/common/sortByType';
import { PricingFormatter } from 'helpers/formatters';

import ORIGIN from '@arrive/ui-icons/svg/ic_up_right_arrow.svg';
import DESTINATION from '@arrive/ui-icons/svg/ic_down_right_arrow.svg';
import DROPDOWN_ARROW from '@arrive/ui-icons/svg/ic_drawer.svg';
import RIGHT_ARROW from '@arrive/ui-icons/svg/ic_right_arrow.svg';

import colors from '@arrive/style-foundation/variables/colors-js.scss';
import styles from './LaneWidget.scss';

const { tandem } = colors;
const foregroundColor = '#FAFAFA';

enum CC_LANE_TYPE {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

const LANE_OPTIONS = [
  { id: CC_LANE_TYPE.ACTIVE, content: 'Active Lanes' },
  { id: CC_LANE_TYPE.INACTIVE, content: 'Inactive Lanes'}
];

interface LanePickerProps {
  selectedLaneType: CC_LANE_TYPE;
  onChange: (newValue: any) => void;
}

const LanePicker = ({ onChange, selectedLaneType }: LanePickerProps) => {
  const [showLanePicker, setShowLanePicker] = useState(false);

  const handleOnChange = (newValue: any) => {
    onChange(newValue);
    setShowLanePicker(false);
  };

  return (
    <SelectableListPopover
      classes={{
        anchorClass: styles.lanePickerContainer,
        contentContainerClass: styles.lanePickerContent
      }}
      onChange={handleOnChange}
      options={LANE_OPTIONS}
      placement='bottom-start'
      selectedOptionIds={[selectedLaneType]}
      setShowPopover={setShowLanePicker}
      showPopover={showLanePicker}
    >
      <div
        className={styles.clickCursor}
        data-testid={'lane-type-picker'}
        onClick={() => setShowLanePicker(!showLanePicker)}
      >
        {LANE_OPTIONS[selectedLaneType === CC_LANE_TYPE.ACTIVE ? 0 : 1].content}
        <SVGIcon svgRef={DROPDOWN_ARROW} />
      </div>
    </SelectableListPopover>
  );
};

// Attribute names for the sort field have to match CC services dto since OriginCity is within the customerlane object and LoadsPW is LoadPerWeek.
//CAREX-3105 was caused by this dto being restructured
enum LANE_SORT_OPTIONS {
  DEFAULT = 'createdOn-ASC',
  ALPHA_ASC = 'customerlane.OriginCity-ASC',
  ALPHA_DSC = 'customerlane.OriginCity-DESC',
  VOL_HIGH = 'LoadPerWeek-DESC',
  VOL_LOW = 'LoadPerWeek-ASC'
}

export const FULL_SORT_OPTIONS: { [key: string]: { id: string; content: string } } = {
  'createdOn-ASC': { id: LANE_SORT_OPTIONS.DEFAULT, content: 'Default (longest ran)' },
  'customerlane.OriginCity-ASC': { id: LANE_SORT_OPTIONS.ALPHA_ASC, content: 'Alphabetical Asc (A to Z)' },
  'customerlane.OriginCity-DESC': { id: LANE_SORT_OPTIONS.ALPHA_DSC, content: 'Alphabetical Dsc (Z to A)' },
  'LoadPerWeek-DESC': { id: LANE_SORT_OPTIONS.VOL_HIGH, content: 'Volume (High to Low)' },
  'LoadPerWeek-ASC': { id: LANE_SORT_OPTIONS.VOL_LOW, content: 'Volume (Low to High)' }
};

export const DEFAULT_SORT = {
  ColumnToSort: 'createdOn',
  SortDirection: SortDirectionEnum.ASC
};

export interface LaneData {
  fsc: {
    rate: number;
    add: boolean;
  }
  carrierRepEmail: string;
  commitmentDaysRemaining: number;
  deliveryDays: string;
  destination: string;
  dropAtConsignee: boolean;
  dropAtShipper: boolean;
  isAutoBook: boolean;
  isEnabled: boolean;
  isHazmat: boolean;
  isTankerEndorsement: boolean;
  isTeam: boolean;
  laneModifiedOn: string;
  leadTime: number;
  loadsPerDay: number;
  loadsPerWeek: number;
  maximumValueAccepted: number;
  origin: string;
  pickupDays: string;
  rate: number;
  rateConEmail: string;
  carrierId: number;
  revenueAddedFsc: number;
  totalLoads: number;
  totalMiles: number;
  truckEquipmentTypeId: number;
  loadTenderEmail: string;
}

const LoadingRows = () => {
  const LOADING_ROWS = 2;
  const rows = [];
  let index = 0;

  const LaneLoader = () => {
    return (
      <ContentLoader
        interval={0.5}
        speed={1.75}
        gradientRatio={0.5}
        width='116px'
        height={'20px'}
        backgroundColor={tandem}
        foregroundColor={foregroundColor}
      >
        <rect x='0' y="1" width={'100px'} height="18"></rect>
      </ContentLoader>
    )
  }

  while (index < LOADING_ROWS) {
    rows.push(<div key={`loading-lane-${index}`} data-testid={`loading-lane-${index}`} className={cx(styles.listItem, styles.loading)} >
      <div className={styles.laneWidthLimit}>
        <div className={styles.laneItem}>
          <LaneLoader />
        </div>
        <div className={styles.laneItem}>
          <LaneLoader />
        </div>
      </div>
      <span className={styles.rateContainer}>
        <ContentLoader
          interval={0.5}
          speed={1.75}
          gradientRatio={0.5}
          width='40px'
          height={'20px'}
          backgroundColor={tandem}
          foregroundColor={foregroundColor}
        >
          <rect x='0' y="1" width={'100%'} height="18"></rect>
        </ContentLoader>
      </span>
    </div>);

    index++;
  }

  return <>{rows}</>;
};

interface LaneWidgetProps {
  data: LaneData[];
  commitment: LaneData;
  setCommitment: (commitment: LaneData) => void;
  setSelectedLaneType: (laneType: CC_LANE_TYPE) => void;
  selectedLaneType: CC_LANE_TYPE;
  setSelectedSort: (sort: SortByType) => void;
  selectedSort: SortByType;
  loading: boolean;
}

const generateLaneRows = (commitment: LaneData, setCommitment: (commitment: LaneData) => void, laneData: LaneData[]) => {
  if (!laneData?.length) {
    return (
      <div key={`lane-no-lanes`} data-testid={`lane-no-lanes`} className={cx(styles.listItem, styles.noInteraction)}>
        Whoa Nellie! No lanes available.
      </div>
    );
  }

  return laneData.map((lane, idx) => {
    const selected = (commitment?.carrierId === lane?.carrierId)
    return (
      <div key={`lane-${idx}`} data-testid={`lane-${idx}`} className={cx(styles.listItem, selected && styles.selected)} onClick={() => setCommitment(lane)}>
        <div className={styles.laneWidthLimit}>
          <div className={styles.laneItem}>
            <SVGIcon className={styles.laneIcon} svgRef={ORIGIN} />{lane.origin}
          </div>
          <div className={styles.laneItem}>
            <SVGIcon className={cx(styles.laneIcon, styles.rightLaneIcon)} svgRef={DESTINATION}/>{lane.destination}
          </div>
        </div>
        <span className={styles.rateContainer}>{PricingFormatter.format(lane.rate)}<SVGIcon className={cx(styles.rightArrowIcon, selected && styles.hideArrow)} svgRef={RIGHT_ARROW}/></span>
      </div>
    )
  });
};

const LaneWidget = ({ data, commitment, setCommitment, setSelectedLaneType, selectedLaneType, setSelectedSort, selectedSort, loading }: LaneWidgetProps) => {
  const handleLaneTypeChange = (newLaneType: [CC_LANE_TYPE]) => setSelectedLaneType(newLaneType[0]);

  return (
    <div className={styles.widgetContainer}>
      <div className={styles.inputsContainer}>
        <LanePicker
          onChange={handleLaneTypeChange}
          selectedLaneType={selectedLaneType}
        />
        <LaneSortingDropdown
          className={styles.sortWidgetContainer}
          testId={'sort-dropdown-lane-type'}
          selectedSort={selectedSort}
          onSortChange={setSelectedSort}
          sortOptions={Object.values(FULL_SORT_OPTIONS)}
        />
      </div>
      <div className={styles.listContainer}>
        { loading && <LoadingRows /> }
        { !loading && generateLaneRows(commitment, setCommitment, data) }
      </div>
    </div>
  )
};

export default LaneWidget;
